import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ANALYSES_META_INFO } from '../../../settings';
import {
  setDeepSEAJobs,
  setSelectedDeepSEAJob,
} from '../../../actions/DeepSEAActions';
import SavedJobsSelector from '../../shared/SavedJobsSelector';
import { sortNewestFirst } from '../../../core/jobs';
import {
  omitKeys,
  removeJobFromLocalStorage,
  loadDeepSEAJobIds,
  saveDeepSEAJobIds,
} from '../../../core/util';

// Define the analysis types based on the keys in ANALYSES_META_INFO
type AnalysisType = keyof typeof ANALYSES_META_INFO;

// Define the interfaces for Job and RootState
interface Job {
  // eslint-disable-next-line camelcase
  job_id: string;
  title?: string;
  created?: string;

  [key: string]: any;
}

interface DeepSEAState {
  selectedJob: Job | null;
  jobs: Record<string, Job>;
}

interface RootState {
  deepsea: DeepSEAState;
}

interface DeepSEAJobSelectorFormProps {
  jobs: Record<string, Job>;
  analysis: AnalysisType; // Update type to be a valid key of ANALYSES_META_INFO
}

const DeepSEAJobSelectorForm: React.FC<DeepSEAJobSelectorFormProps> = ({
  jobs,
  analysis,
}) => {
  const selectedJob = useSelector(
    (state: RootState) => state.deepsea.selectedJob,
  );
  const allJobs = useSelector((state: RootState) => state.deepsea.jobs);
  const dispatch = useDispatch();
  const sortedJobs = sortNewestFirst(Object.values(jobs));

  const handleSelectionChange = (jobId: string) => {
    dispatch(setSelectedDeepSEAJob(jobs[jobId]));
  };

  const handleClear = () => {
    // Clear selected job
    dispatch(setSelectedDeepSEAJob({} as Job));

    // Remove jobs for this analysis from localStorage
    Object.keys(jobs).forEach(jobId => {
      removeJobFromLocalStorage(jobId);
    });
    saveDeepSEAJobIds(
      (loadDeepSEAJobIds() || []).filter(jobId => !jobs[jobId]),
    );

    // Update Redux state to remove jobs for this analysis
    dispatch(setDeepSEAJobs(omitKeys(allJobs, Object.keys(jobs))));
  };

  // Set first job as selected if no job is selected
  useEffect(() => {
    if (!selectedJob && Object.keys(jobs).length) {
      dispatch(setSelectedDeepSEAJob(sortNewestFirst(Object.values(jobs))[0]));
    }
  }, [jobs, selectedJob, dispatch]);

  return (
    <SavedJobsSelector
      heading={`Recent ${ANALYSES_META_INFO[analysis].name} jobs`}
      selectId="formDeepSEASelectedJobId"
      jobs={sortedJobs.map(job => ({ id: job.job_id, title: job.title }))}
      selectedId={selectedJob?.job_id}
      onChange={handleSelectionChange}
      onClear={handleClear}
    />
  );
};

export default DeepSEAJobSelectorForm;
